import React, { useEffect, useRef, useState } from "react";
import { cn } from "@src/lib/utils/cn";
import Text from "@src/lib/ui/Text";
import { Button } from "@src/lib/ui/Button";
import { Textarea } from "@src/lib/ui/Textarea";
import SquareImageUploadButton from "@src/lib/ui/SquareImageUploadButton";
import { useGenerateImageMutation } from "@src/features/app/dialogs/addMedia/AddMediaDialog.generated";
import { OpusAgentAvatarKind } from "@src/types.generated";
import { AgentBot, AGENT_COLORS, type AgentColorSlug } from "./AgentAvatar";

/** What the dialog saves: a colour for the bot, or a round picture. */
export type AgentAvatarChoice =
  | { kind: "color"; color: AgentColorSlug }
  | { kind: "image"; url: string };

export const avatarImagePrompt = (agentName: string): string =>
  `A friendly, simple mascot avatar for an assistant called "${
    agentName.trim() || "my agent"
  }". Centered, flat colors, soft background, no text.`;

type Props = {
  value: AgentAvatarChoice;
  onChange: (value: AgentAvatarChoice) => void;
  /** True while an upload or a generation is under way. */
  onBusyChange?: (busy: boolean) => void;
  /** Seeds the image prompt. */
  agentName: string;
};

/**
 * The agent's avatar: a live preview beside the bot's colours, and an image
 * the admin uploads (cropped square) or generates with Opus AI. Picking a
 * colour goes back to the bot.
 */
const AgentAvatarPicker: React.FC<Props> = ({
  value,
  onChange,
  onBusyChange,
  agentName,
}) => {
  const [generateOpen, setGenerateOpen] = useState(false);
  // Arrow keys pick the next or previous colour, as in any radio group.
  const moveBetweenSwatches = (
    event: React.KeyboardEvent<HTMLButtonElement>,
  ) => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[
      event.key
    ];
    if (!step) return;
    event.preventDefault();
    const current = AGENT_COLORS.findIndex(
      (color) => value.kind === "color" && color.slug === value.color,
    );
    const next =
      AGENT_COLORS[
        (Math.max(current, 0) + step + AGENT_COLORS.length) %
          AGENT_COLORS.length
      ];
    onChange({ kind: "color", color: next.slug });
    event.currentTarget.parentElement
      ?.querySelector<HTMLElement>(
        `[data-testid="agent-avatar-color-${next.slug}"]`,
      )
      ?.focus();
  };
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  useEffect(() => {
    onBusyChange?.(uploading || generating);
  }, [uploading, generating, onBusyChange]);
  return (
    <div className="flex flex-col gap-1.5" data-testid="agent-avatar-picker">
      <Text variant="compact-title">Avatar</Text>
      <div className="flex items-start gap-4 rounded-xl border border-border-primary p-3">
        <div className="flex size-16 shrink-0 items-center justify-center rounded-xl bg-bg-secondary">
          <AgentBot
            agent={
              value.kind === "image"
                ? {
                    id: "preview",
                    avatarKind: OpusAgentAvatarKind.Image,
                    avatarImageUrl: value.url,
                  }
                : {
                    id: "preview",
                    avatarKind: OpusAgentAvatarKind.Bot,
                    avatarBotColor: value.color,
                  }
            }
            size={48}
            label="Avatar preview"
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <div
            className="flex flex-wrap gap-2"
            role="radiogroup"
            aria-label="Color"
          >
            {AGENT_COLORS.map((color, index) => {
              const selected =
                value.kind === "color" && value.color === color.slug;
              return (
                <button
                  key={color.slug}
                  type="button"
                  aria-label={color.label}
                  title={color.label}
                  role="radio"
                  onKeyDown={moveBetweenSwatches}
                  aria-checked={selected}
                  // One tab stop: the chosen colour, or the first when an
                  // image is the avatar; arrows move between them.
                  tabIndex={
                    selected || (value.kind === "image" && index === 0) ? 0 : -1
                  }
                  data-testid={`agent-avatar-color-${color.slug}`}
                  onClick={() => onChange({ kind: "color", color: color.slug })}
                  className={cn(
                    "size-6 rounded-full border border-border-primary ring-offset-2 ring-offset-bg-primary transition-shadow outline-none focus-visible:ring-2 focus-visible:ring-state-focus",
                    selected
                      ? "ring-2 ring-border-tertiary"
                      : "hover:ring-2 hover:ring-border-primary",
                  )}
                  style={{ background: color.color }}
                />
              );
            })}
          </div>
          <div className="flex flex-wrap gap-2">
            <SquareImageUploadButton
              text="Upload image"
              variant="outline"
              onNewImageUrl={(url) => onChange({ kind: "image", url })}
              onUploadingChange={setUploading}
            />
            <Button
              variant="outline"
              aria-expanded={generateOpen}
              onClick={() => setGenerateOpen((open) => !open)}
              data-testid="agent-avatar-generate-toggle"
            >
              Generate image
            </Button>
          </div>
          {generateOpen && (
            <GenerateControls
              agentName={agentName}
              loading={generating}
              onLoadingChange={setGenerating}
              onGenerated={(url) => onChange({ kind: "image", url })}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const GenerateControls: React.FC<{
  agentName: string;
  onGenerated: (url: string) => void;
  /** Held by the picker, so a reopened panel shows a request in flight. */
  loading: boolean;
  onLoadingChange: (loading: boolean) => void;
}> = ({ agentName, onGenerated, loading, onLoadingChange }) => {
  const [prompt, setPrompt] = useState(() => avatarImagePrompt(agentName));
  // A request still running when the panel closes keeps Save held until it
  // ends, but its image no longer replaces whatever was picked since.
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );
  const [error, setError] = useState<string | null>(null);
  const [generateImage] = useGenerateImageMutation();

  const generate = async () => {
    setError(null);
    onLoadingChange(true);
    try {
      const result = await generateImage({
        variables: { input: { prompt: prompt.trim() } },
      });
      const url = result.data?.generateImage?.imageUrl;
      if (!mounted.current) return;
      if (result.data?.generateImage?.success && url) onGenerated(url);
      else setError("Couldn't generate an image. Try another description.");
    } catch {
      if (mounted.current)
        setError("Couldn't generate an image. Please try again.");
    } finally {
      onLoadingChange(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <Textarea
        value={prompt}
        onChange={(event) => setPrompt(event.target.value)}
        minRows={2}
        maxRows={4}
        autoSize
        aria-label="Image description"
        data-testid="agent-avatar-image-prompt"
      />
      <div className="flex items-center justify-between gap-2">
        <Text variant="helper-description" className="text-text-tertiary">
          Uses Opus AI credits
        </Text>
        <Button
          variant="secondary"
          size="sm"
          loading={loading}
          disabled={!prompt.trim() || loading}
          onClick={generate}
          data-testid="agent-avatar-image-generate"
        >
          Generate
        </Button>
      </div>
      {error && (
        <Text
          variant="helper-description"
          className="text-status-error-text"
          multiline
        >
          {error}
        </Text>
      )}
    </div>
  );
};

export default AgentAvatarPicker;
